<script setup>
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'

const page = usePage()
const platformFee = computed(() => Number(page.props.config?.platform_fee_percentage))
</script>

<template>
  <div class="prose prose-lg max-w-none">
    <p class="text-gray-600 mb-6">Last updated: 11.10.2026</p>

    <p class="text-gray-700 mb-8">
      These Tutor Terms & Conditions ("Terms") govern your participation as a tutor on the Bindle platform.
    </p>

    <p class="text-gray-700 mb-8">
      By ticking the box "I agree to the Tutor Terms & Conditions", you confirm that you have read, understood, and agree to be legally bound by these Terms.
    </p>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">1. About Bindle</h2>
      <p class="text-gray-700 mb-4">
        Bindle Resources Ltd ("Bindle", "we", "us") operates an online education platform that enables tutors to publish educational content and provide tutoring services to students through subscription-based products.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">2. Status of Tutors</h2>
      <p class="text-gray-700 mb-4">
        You engage with Bindle strictly as an independent contractor.
      </p>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>You are not an employee, worker, agent, partner, or representative of Bindle.</li>
        <li>You are responsible for all taxes and statutory obligations.</li>
        <li>Bindle is not responsible for your conduct or services provided to students.</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">3. Tutor Content & Intellectual Property</h2>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>You retain ownership of all intellectual property rights in content you upload ("Tutor Content").</li>
        <li>You grant Bindle a perpetual, worldwide, royalty-free licence to host, display, distribute, modify, and use Tutor Content for platform, operational, AI, and marketing purposes.</li>
        <li>Tutor Content must remain exclusive to Bindle while available on the platform.</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">4. Video Upload Limits</h2>
      <p class="text-gray-700 mb-4">
        Tutors may upload up to 20 hours of video content free of charge. Additional storage may be charged at £0.0024 per minute.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">5. Subscriptions, Pricing & Payments</h2>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>Tutors may set their session price within boundaries set by Bindle. A new price applies to new subscriptions only.</li>
        <li>You cannot take bookings until your payout account with our payment provider is fully set up. If your payout account stops being able to receive payments, you are hidden from new bookings until it is fixed.</li>
        <li>Bindle retains a {{ platformFee }}% platform fee, unless another rate has been agreed with you in writing; you receive the rest.</li>
        <li>Students are charged for each session 48 hours before it starts. You are paid for a session once its outcome is confirmed (see section 6), any complaint about it has been decided, and the student's payment has cleared. Payments are sent every Sunday evening to your payout account for every session ready by then; anything not ready is sent the following Sunday. Our payment provider sends it on to your bank the next morning; most banks show it that day, some a day later.</li>
        <li>If a student pays part of a session with credit given by Bindle, you are still paid on the full session price.</li>
        <li>If a student's payment for a session fails, the session still goes ahead. You are paid for it once the student pays; if they have not paid 7 days after it, Bindle decides how to proceed.</li>
        <li>Where you cancel a session with less than 48 hours' notice, or do not attend, the £5 credit the student receives is deducted from your next payment, unless the student's payment for the session had failed: the student then receives no credit, and nothing is deducted.</li>
        <li>While your payout account cannot receive payments, what you are owed is held and sent once it can. If it cannot be fixed, Bindle may end your subscriptions; students are then refunded in full for sessions already paid for, and you are not paid for those sessions.</li>
        <li>If your bank refuses a payment, we will email you. The money stays in your payout account until your bank details are corrected.</li>
        <li>If a student's payment is refunded or disputed after you have been paid for it, your share may be deducted from your later payments. If a dispute is decided in Bindle's favour, the amount deducted is paid back to you. Anything you owe Bindle that later payments cannot cover, for example because you have stopped teaching, Bindle may ask you to pay back.</li>
        <li>A student may report a problem with a session within 7 days of it. Bindle reviews it; where Bindle finds the problem was yours, the refund it gives the student is deducted from what you are paid for that session, or from your later payments if you have already been paid.</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">6. Sessions, Attendance & Cancellation</h2>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>Sessions take place in the platform's lesson room, which records who attends and when.</li>
        <li>A session counts as delivered when you are in the lesson room within the first 15 minutes of it and the student joins, even late. It also counts as delivered, and you are paid for it, when you join later but within the first half of the session and the student is in the room with you; arriving late is against Bindle's tutor policy, and Bindle may give you a warning for it.</li>
        <li>If you join on time and the student does not attend, you are paid in full. If you agree to refund the student, ask Bindle, and the refund is deducted from your pay.</li>
        <li>If you do not attend, the student is refunded in full, you are not paid for the session, the student's £5 credit is deducted from your pay, and you receive a warning. If the student's payment for the session had failed, they are no longer charged for it, and you receive the warning only.</li>
        <li>Any other case, such as joining only in the second half of the session or neither party attending, is reviewed by Bindle before you are paid. Bindle decides whether the session took place. A session that fails through no one's fault, such as a problem with the lesson room, carries no warning and no deduction.</li>
        <li>You are expected to give at least 48 hours' notice to cancel a session, and must tell the student why; your message is sent to them. Cancelling with less notice is against Bindle's tutor policy: the student is refunded in full and given £5 of credit, which is deducted from your next payment, and you receive a warning. None of this applies if the student's payment for the session had failed. With more notice, cancelling costs you nothing.</li>
        <li>If a student cancels more than 48 hours before a session, nothing is paid. If they cancel within 48 hours, the student is charged the full session price and you are paid your share of it once their payment goes through.</li>
        <li>Moving a session, adding a session or changing the weekly time, including through changes to your calendar, is a request the student must accept. Until they do, the session stays as booked.</li>
        <li>Students may pause a subscription for up to 4 weeks at a time and up to 8 weeks in any 12 months. You are not paid for sessions during a pause, and the weekly slot is kept for the student: it cannot be offered to anyone else meanwhile. Once a pause is arranged, sessions cannot be moved or added until it ends.</li>
        <li>You or the student may end a subscription at any time, from your own account; if you end it, you must tell the student why. It ends immediately, and any session within 48 hours or already paid for is cancelled on the terms above, as cancelled by whoever ended it.</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">7. Conduct & Safeguarding</h2>
      <ul class="list-disc pl-6 text-gray-700 mb-4 space-y-2">
        <li>Tutors must act professionally and deliver sessions as scheduled.</li>
        <li>Each late cancellation of a session whose payment had not failed, and each missed session, earns a warning, and a late arrival may earn one; we email you each time. A tutor with 3 warnings may be removed from Bindle. A warning Bindle finds was not your doing is set aside.</li>
        <li>Tutors must hold a valid Enhanced DBS certificate (issued within the last 24 months) and provide ID.</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">8. Non-Solicitation</h2>
      <p class="text-gray-700 mb-4">
        Tutors must not solicit students off-platform during engagement or for 12 months after termination.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">9. AI & Analytics</h2>
      <p class="text-gray-700 mb-4">
        Bindle may analyse Tutor Content and usage data to improve the platform.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">10. Termination</h2>
      <p class="text-gray-700 mb-4">
        Either party may terminate with 30 days' notice. Bindle may terminate immediately for serious breach.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">11. Liability</h2>
      <p class="text-gray-700 mb-4">
        Tutors indemnify Bindle against claims arising from Tutor Content or services.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">12. Governing Law</h2>
      <p class="text-gray-700 mb-4">
        These Terms are governed by the laws of England and Wales.
      </p>
    </section>
  </div>
</template>
